import SEO from '@/components/SEO';
import MarketingHeader from '@/components/layout/MarketingHeader';
import MarketingFooter from '@/components/layout/MarketingFooter';

const PrivacyPolicyPage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy - Review Plum',
    description: 'Learn how Review Plum collects, uses, and protects your personal information. Our privacy policy explains your rights and our data practices.',
    url: 'https://reviewplum.com/privacy-policy',
    publisher: {
      '@type': 'Organization',
      name: 'Review Plum',
      url: 'https://reviewplum.com',
    },
    dateModified: '2026-01-20',
  };

  return (
    <>
      <SEO
        title="Privacy Policy - Review Plum | How We Protect Your Data"
        description="Learn how Review Plum collects, uses, and protects your personal information. Our privacy policy explains your data rights and our commitment to security."
        keywords={['privacy policy', 'data protection', 'review plum privacy', 'user data security', 'GDPR compliance']}
        canonicalUrl="https://reviewplum.com/privacy-policy"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        
        <main className="container mx-auto px-4 py-16 max-w-4xl">
          <article itemScope itemType="https://schema.org/WebPage">
            <header className="mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4" itemProp="name">
                Privacy Policy
              </h1>
              <p className="text-muted-foreground">
                Last updated: January 20, 2026
              </p>
            </header>

            <div className="prose prose-lg max-w-none space-y-8" itemProp="text">
              <section aria-labelledby="introduction">
                <h2 id="introduction" className="text-2xl font-semibold text-foreground mb-4">
                  1. Introduction
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Review Plum ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered Google review generation platform and related services.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  By using Review Plum, you consent to the data practices described in this policy. If you do not agree with these practices, please do not use our services.
                </p>
              </section>

              <section aria-labelledby="information-collected">
                <h2 id="information-collected" className="text-2xl font-semibold text-foreground mb-4">
                  2. Information We Collect
                </h2>
                
                <h3 className="text-xl font-medium text-foreground mb-3">2.1 Personal Information</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We may collect personal information that you voluntarily provide when registering for our services, including:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Name and email address</li>
                  <li>Business name, address, and contact information</li>
                  <li>Google Business Profile URL</li>
                  <li>Payment and billing information</li>
                  <li>Industry and business category</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">2.2 Usage Data</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We automatically collect certain information when you use our platform:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and features used</li>
                  <li>Review generation history and preferences</li>
                  <li>Time spent on our platform</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">2.3 Cookies and Tracking</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your experience. You can control cookie preferences through your browser settings. Essential cookies are required for basic platform functionality.
                </p>
              </section>

              <section aria-labelledby="how-we-use">
                <h2 id="how-we-use" className="text-2xl font-semibold text-foreground mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use the collected information for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>To provide and maintain our AI-powered review generation services</li>
                  <li>To personalize review suggestions based on your business profile</li>
                  <li>To process payments and manage subscriptions</li>
                  <li>To communicate with you about updates, offers, and support</li>
                  <li>To improve our platform and develop new features</li>
                  <li>To detect and prevent fraudulent activities</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section aria-labelledby="data-sharing">
                <h2 id="data-sharing" className="text-2xl font-semibold text-foreground mb-4">
                  4. Information Sharing and Disclosure
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We do not sell your personal information. We may share your data with:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (payment processors, hosting providers, analytics services)</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                </ul>
              </section>

              <section aria-labelledby="data-security">
                <h2 id="data-security" className="text-2xl font-semibold text-foreground mb-4">
                  5. Data Security
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement industry-standard security measures to protect your information, including:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>SSL/TLS encryption for data in transit</li>
                  <li>Encrypted database storage</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Access controls and authentication protocols</li>
                  <li>Secure cloud infrastructure hosted on reputable providers</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  While we strive to protect your data, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
                </p>
              </section>

              <section aria-labelledby="your-rights">
                <h2 id="your-rights" className="text-2xl font-semibold text-foreground mb-4">
                  6. Your Rights and Choices
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                  <li><strong>Portability:</strong> Request your data in a portable format</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  To exercise these rights, contact us at arpit@reviewplum.com. We will respond within 30 days.
                </p>
              </section>

              <section aria-labelledby="data-retention">
                <h2 id="data-retention" className="text-2xl font-semibold text-foreground mb-4">
                  7. Data Retention
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide services. After account deletion, we may retain certain data for legal compliance, dispute resolution, or enforcement of agreements, typically for up to 3 years.
                </p>
              </section>

              <section aria-labelledby="third-party">
                <h2 id="third-party" className="text-2xl font-semibold text-foreground mb-4">
                  8. Third-Party Links
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our platform may contain links to third-party websites, including Google Business Profile. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </section>

              <section aria-labelledby="children">
                <h2 id="children" className="text-2xl font-semibold text-foreground mb-4">
                  9. Children's Privacy
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Review Plum is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us immediately.
                </p>
              </section>

              <section aria-labelledby="international">
                <h2 id="international" className="text-2xl font-semibold text-foreground mb-4">
                  10. International Data Transfers
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
                </p>
              </section>

              <section aria-labelledby="changes">
                <h2 id="changes" className="text-2xl font-semibold text-foreground mb-4">
                  11. Changes to This Policy
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy periodically. We will notify you of significant changes by posting a notice on our platform or sending an email. Your continued use after changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section aria-labelledby="contact">
                <h2 id="contact" className="text-2xl font-semibold text-foreground mb-4">
                  12. Contact Us
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-4 p-6 bg-muted/50 rounded-lg">
                  <p className="text-foreground font-medium">Review Plum</p>
                  <p className="text-muted-foreground">Email: arpit@reviewplum.com</p>
                  <p className="text-muted-foreground">Website: https://reviewplum.com</p>
                </div>
              </section>
            </div>
          </article>
        </main>
        
        <MarketingFooter />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
